import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

/** Horizon-aggregate panel. Reads /api/today/horizon for the given
 *  horizon and renders the rows. The picker that used to live here
 *  was promoted to the page-level L2RailShell on Today.tsx so
 *  Today/This Week/This Month read as proper sub-views of the Now
 *  section rather than a pill strip inside one card. */
export type HorizonKey = "day" | "week" | "month";

interface Aggregate {
  horizon: string;
  kind: string;
  payload_json: string;
  window_start: string;
  window_end: string;
  generated_at: string;
}

export function HorizonAggregates({ horizon }: { horizon: HorizonKey }) {
  const [aggs, setAggs] = useState<Aggregate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/today/horizon?horizon=${horizon}`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { aggregates: [] }))
      .then((j) => setAggs(j.aggregates ?? []))
      .catch(() => setAggs([]))
      .finally(() => setLoading(false));
  }, [horizon]);

  return (
    <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-3">
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading horizon…
        </div>
      ) : aggs.length === 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Nothing aggregated for this horizon yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {aggs.slice(0, 24).map((a, i) => {
            let summary = a.kind;
            try {
              const p = JSON.parse(a.payload_json || "{}");
              summary = p.summary || p.headline || p.title || a.kind;
            } catch {
              /* keep kind */
            }
            return (
              <li
                key={`${a.kind}-${i}`}
                className="text-sm text-slate-700 dark:text-slate-200 flex items-start gap-2"
              >
                <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 mt-0.5">
                  {a.kind}
                </span>
                <span className="min-w-0">{summary}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
